import React from 'react';
import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { withMediaSipContext } from '../../contexts/MediaSipContext';
import { styled } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import * as stateActions from '../../actions/stateActions';
import * as requestActions from '../../actions/requestActions';
import * as sessionStates from '../../sessionStates';
import Grid from '@mui/material/Grid';
import InputBase from '@mui/material/InputBase';
import Button from '@mui/material/Button';
import CallIcon from '@mui/icons-material/Call';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import ErrorIcon from '@mui/icons-material/Error';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import LocalVideoPreviewer from './LocalVideoPreviewer';

const DialerWrapper = styled(Paper)(({ theme }) => ({
	padding: theme.spacing(2),
	borderRadius: theme.spacing(2),
	maxWidth: '600px',
	minWidth: '450px',
	margin: '0 auto',
	[theme.breakpoints.down('sm')]: {
		padding: theme.spacing(1.5),
		margin: theme.spacing(1),
		width: '80vw',
		maxWidth: 'none',
		minWidth: '300px'
	}
}));

const GridStyled = styled(Grid)({
	width: '100%',
	margin: 0
});

const Call = styled(Box)(() => ({
	display: 'flex',
	alignItems: 'center',
	flex: 1,
	width: '100%',
	position: 'relative',
}));

const CallIconWrapper = styled(Box)(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	padding: theme.spacing(1),
	color: theme.palette.primary.main,
	position: 'absolute',
	left: 0,
	top: '50%',
	transform: 'translateY(-50%)',
	zIndex: 1,
}));

const InputBaseStyled = styled(InputBase)(({ theme }) => ({
	color: 'inherit',
	width: '100%',
	minWidth: '150px',
	'& .MuiInputBase-input': {
		paddingTop: theme.spacing(1.5),
		paddingRight: theme.spacing(1),
		paddingBottom: theme.spacing(1.5),
		paddingLeft: theme.spacing(5),
		transition: theme.transitions.create('width'),
		width: '100%',
		fontSize: '1.1rem'
	}
}));

const InputContainer = styled(Box)(({ theme }) => ({
	display: 'flex',
	flexDirection: 'column',
	gap: theme.spacing(1.5),
	width: '100%',
	[theme.breakpoints.up('sm')]: {
		flexDirection: 'row',
		alignItems: 'center',
	}
}));

const ActionButton = styled(Button)(({ theme }) => ({
	minWidth: '200px',
	height: '48px',
	borderRadius: '24px',
	fontSize: '1.1rem',
	textTransform: 'none',
	boxShadow: 3,
	'&:hover': {
		boxShadow: 6,
		transform: 'translateY(-1px)',
		transition: 'all 0.2s ease-in-out'
	},
	[theme.breakpoints.down('sm')]: {
		width: '100%',
		minWidth: 'unset',
	}
}));

const SpinnerContainer = styled(Box)(({ theme }) => ({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	backgroundColor: 'rgba(255, 255, 255, 0.9)',
	padding: theme.spacing(4),
	borderRadius: theme.spacing(2),
	boxShadow: theme.shadows[3],
}));


const Dialer = (props) => {
	const { sipCaller } = props;
	const { enqueueSnackbar } = useSnackbar();

	const dispatch = useDispatch();
	const requestUri = useSelector(state => state.user.requestUri);
	const registered = useSelector(state => state.userStatus.registered);
	const sessions = useSelector(state => state.sessions);
	const currentSessionId = useSelector(state => state.userStatus.currentSession);
	const notifications = useSelector(state => state.notifications || []);

	const [connecting, setConnecting] = useState(false);
	const [callEstablished, setCallEstablished] = useState(false);
	const [connectionError, setConnectionError] = useState(null);
	const currentSession = useRef(null);
	const connectionTimeout = useRef(null);
	const noSessionErrorTimeout = useRef(null);
	const [networkType, setNetworkType] = useState('urgentnet');

	// Monitor for connection errors in notifications
	useEffect(() => {
		// Look for error notifications while connecting
		if (connecting && !callEstablished) {
			const errors = notifications.filter(note =>
				note.type === 'error' &&
				(note.text.includes('call') ||
					note.text.includes('session') ||
					note.text.includes('ICE') ||
					note.text.includes('connection') ||
					note.text.includes('SIP') ||
					note.text.includes('media') ||
					note.text.includes('Failed') ||
					note.text.includes('failed'))
			);

			// If there's a new error notification, show it and update our error state
			if (errors.length > 0) {
				const latestError = errors[errors.length - 1];
				setConnectionError(latestError.text);

				// Cancel the connection
				setConnecting(false);

				// Show error in snackbar
				enqueueSnackbar(latestError.text, {
					variant: 'error',
					anchorOrigin: {
						vertical: 'top',
						horizontal: 'center',
					}
				});
			}
		}
	}, [notifications, connecting, callEstablished, enqueueSnackbar]);

	// In Dialer.js, find the connection timeout effect (around line 70-100)
	useEffect(() => {
		if (connecting && !callEstablished) {
			// Increase timeout from 30 seconds to 120 seconds
			connectionTimeout.current = setTimeout(() => {
				if (connecting && !callEstablished) {
					const errorMessage = 'Call connection timed out. Please check your network and try again.';
					setConnectionError(errorMessage);
					setConnecting(false);

					enqueueSnackbar(errorMessage, {
						variant: 'error',
						anchorOrigin: {
							vertical: 'top',
							horizontal: 'center',
						}
					});

					// If there's a session in progress, try to terminate it
					if (currentSession.current && currentSession.current.sipSession) {
						try {
							sipCaller.terminate(currentSession.current.sipSession);
						} catch (error) {
							console.error('Failed to terminate timed out call:', error);
						}
					}
				}
			}, 120000); // Increase from 30000 to 120000

			return () => {
				if (connectionTimeout.current) {
					clearTimeout(connectionTimeout.current);
				}
			};
		}
	}, [connecting, callEstablished, enqueueSnackbar, sipCaller]);

	// Track call state changes
	useEffect(() => {
		// Check if we have a current session
		if (currentSessionId && sessions[currentSessionId]) {
			const session = sessions[currentSessionId];
			currentSession.current = session;

			// If session has a remoteStream with audio tracks, consider it established
			if (session.remoteStream &&
				session.remoteStream.getAudioTracks &&
				session.remoteStream.getAudioTracks().length > 0) {
				setCallEstablished(true);
				setConnecting(false);
				setConnectionError(null);

				// Clear any timeouts
				if (connectionTimeout.current) {
					clearTimeout(connectionTimeout.current);
				}
				if (noSessionErrorTimeout.current) {
					clearTimeout(noSessionErrorTimeout.current);
					noSessionErrorTimeout.current = null;
				}
			}
			// If session state is NEW or ESTABLISHING, it's still connecting
			else if (session.sessionState === sessionStates.NEW ||
				session.sessionState === sessionStates.ESTABLISHING) {
				setConnecting(true);
				setCallEstablished(false);
			}
			// Check if session is terminated with an error
			else if (session.sessionState === sessionStates.TERMINATED && connecting) {
				setConnecting(false);

				// If no specific error was caught earlier, show a generic message
				if (!connectionError) {
					const errorMessage = 'Call failed to connect';
					setConnectionError(errorMessage);
					enqueueSnackbar(errorMessage, {
						variant: 'error',
						anchorOrigin: {
							vertical: 'top',
							horizontal: 'center',
						}
					});
				}
			}
		} else {
			// No current session
			// But check if there are any sessions that might be establishing
			const allSessionIds = Object.keys(sessions);
			const hasAnySessions = allSessionIds.length > 0;
			
			// Check if any session is ESTABLISHED or has remoteStream (call is working)
			let hasEstablishedSession = false;
			if (hasAnySessions) {
				for (const sessionId of allSessionIds) {
					const session = sessions[sessionId];
					if (session) {
						// Check if session is established or has remote stream
						if (session.sessionState === sessionStates.ESTABLISHED ||
							(session.remoteStream && 
							 session.remoteStream.getAudioTracks && 
							 session.remoteStream.getAudioTracks().length > 0)) {
							hasEstablishedSession = true;
							// If we found an established session but currentSessionId doesn't match,
							// set it to this session
							if (currentSessionId !== sessionId) {
								dispatch(stateActions.setCurrentSession({ currentSession: sessionId }));
							}
							break;
						}
					}
				}
			}
			
			if (hasEstablishedSession) {
				// Session is established, clear connecting state
				currentSession.current = sessions[allSessionIds[0]];
				setCallEstablished(true);
				setConnecting(false);
				setConnectionError(null);
				
				// Clear any timeouts
				if (connectionTimeout.current) {
					clearTimeout(connectionTimeout.current);
				}
				if (noSessionErrorTimeout.current) {
					clearTimeout(noSessionErrorTimeout.current);
					noSessionErrorTimeout.current = null;
				}
			} else {
				// No established session found
				currentSession.current = null;

				// If we were connecting but the session disappeared, check if it's a real error
				// Add a delay to avoid false positives during initial session creation
				// Note: _buildInitialStreamWithAvatar() is async, so session creation may take longer
				if (connecting && !connectionError) {
					// If there are sessions but none are established yet, they're still connecting
					if (hasAnySessions) {
						// Clear timeout - session exists and is connecting
						if (noSessionErrorTimeout.current) {
							clearTimeout(noSessionErrorTimeout.current);
							noSessionErrorTimeout.current = null;
						}
					} else {
						// No sessions yet - set timeout to check for error
						// Clear any existing timeout
						if (noSessionErrorTimeout.current) {
							clearTimeout(noSessionErrorTimeout.current);
						}

						// Increase delay to 5 seconds to account for async avatar stream creation + direct call setup
						noSessionErrorTimeout.current = setTimeout(() => {
							// The useEffect will re-run if sessions change, which will cancel this timeout
							const errorMessage = 'Call connection failed';
							setConnectionError(errorMessage);
							enqueueSnackbar(errorMessage, {
								variant: 'error',
								anchorOrigin: {
									vertical: 'top',
									horizontal: 'center',
								}
							});
							setConnecting(false);
							noSessionErrorTimeout.current = null;
						}, 5000); // 5 second delay to allow async avatar stream creation + session setup
					}
				} else if (!connecting) {
					// Not connecting anymore, clear timeout
					if (noSessionErrorTimeout.current) {
						clearTimeout(noSessionErrorTimeout.current);
						noSessionErrorTimeout.current = null;
					}
				}

				if (!connecting) {
					setCallEstablished(false);
				}
			}
		}
	}, [sessions, currentSessionId, connecting, connectionError, enqueueSnackbar, dispatch]);

	// Reset states when sessions are empty
	useEffect(() => {
		if (Object.keys(sessions).length === 0) {
			setConnecting(false);
			setCallEstablished(false);
			setConnectionError(null);

			// Clear any timeout
			if (connectionTimeout.current) {
				clearTimeout(connectionTimeout.current);
			}
			if (noSessionErrorTimeout.current) {
				clearTimeout(noSessionErrorTimeout.current);
				noSessionErrorTimeout.current = null;
			}
		}
	}, [sessions]);

	const setRequestUri = (requestUri) => {
		dispatch(stateActions.setRequestUri({ requestUri }));
	};

	const handleCall = () => {
		if (registered && requestUri) {
			// Reset error state
			setConnectionError(null);

			// Set connecting state
			setConnecting(true);

			try {
				// Attempt to make the call
				if (networkType === "urgentnet") sipCaller.invite(requestUri + "@urgentnet.works");
				else if (networkType === 'healthnet') sipCaller.invite(requestUri + "@healthnet.works");
				else sipCaller.invite(requestUri + "@urgent-route.com");
			} catch (error) {
				// If there's an immediate error, handle it
				console.error('Call initiation error:', error);
				setConnecting(false);

				const errorMessage = `Failed to start call: ${error.message || 'Unknown error'}`;
				setConnectionError(errorMessage);

				// Show snackbar
				enqueueSnackbar(errorMessage, {
					variant: 'error',
					anchorOrigin: {
						vertical: 'top',
						horizontal: 'center',
					}
				});

				// Also dispatch to store so other components can react
				dispatch(requestActions.notify({
					type: 'error',
					text: errorMessage
				}));
			}
		}
	};

	// Only show the spinner while connecting (not when call is established)
	if (connecting && !callEstablished) {
		return (
			<SpinnerContainer>
				<CircularProgress color="primary" size={50} thickness={4} />
				<Typography variant="h6" style={{ marginTop: 16 }}>
					Connecting...
				</Typography>
				<Button
					color="secondary"
					variant="outlined"
					onClick={() => {
						// Cancel the call attempt
						setConnecting(false);

						// If there's an active session, terminate it
						if (currentSession.current && currentSession.current.sipSession) {
							sipCaller.terminate(currentSession.current.sipSession);
						}
					}}
					style={{ marginTop: 16 }}
				>
					Cancel
				</Button>
			</SpinnerContainer>
		);
	}

	// Show error state if connection failed
	if (connectionError) {
		return (
			<SpinnerContainer>
				<ErrorIcon color="error" style={{ fontSize: 50 }} />
				<Typography variant="h6" style={{ marginTop: 16, color: '#f44336' }}>
					Connection Error
				</Typography>
				<Typography variant="body2" style={{ marginTop: 8, textAlign: 'center', maxWidth: '80%' }}>
					{connectionError}
				</Typography>
				<Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1, width: '100%', maxWidth: '240px' }}>
					<Button
						color="primary"
						variant="contained"
						onClick={() => {
							// Reset error and show dialer again
							setConnectionError(null);
						}}
						fullWidth
					>
						Try Again
					</Button>

					<Button
						color="secondary"
						variant="outlined"
						onClick={() => {
							// Try refreshing the SIP client connection
							if (sipCaller && sipCaller.unRegister && sipCaller.register) {
								try {
									// Unregister and re-register to reset the SIP connection
									sipCaller.unRegister();
									setTimeout(() => sipCaller.register(), 1000);

									enqueueSnackbar('Refreshing SIP connection...', {
										variant: 'info',
										anchorOrigin: {
											vertical: 'top',
											horizontal: 'center',
										}
									});

									// Reset error state
									setConnectionError(null);
								} catch (error) {
									console.error('Failed to refresh SIP connection:', error);
								}
							}
						}}
						fullWidth
					>
						Refresh Connection
					</Button>

					<Button
						color="info"
						variant="outlined"
						onClick={async () => {
							// Run network diagnostics
							if (sipCaller && sipCaller.checkNetworkConnectivity) {
								enqueueSnackbar('Running network diagnostics...', {
									variant: 'info',
									anchorOrigin: {
										vertical: 'top',
										horizontal: 'center',
									}
								});

								try {
									// Run the diagnostics
									const results = await sipCaller.checkNetworkConnectivity();

									if (results.success) {
										// Network is fine, might be another issue
										enqueueSnackbar('Network connectivity looks good. The issue may be with the remote endpoint.', {
											variant: 'info',
											anchorOrigin: {
												vertical: 'top',
												horizontal: 'center',
											}
										});
									}
									// The function will already display an error if there are issues
								} catch (error) {
									console.error('Error running diagnostics:', error);
									enqueueSnackbar('Failed to run network diagnostics', {
										variant: 'error',
										anchorOrigin: {
											vertical: 'top',
											horizontal: 'center',
										}
									});
								}
							} else {
								enqueueSnackbar('Diagnostics not available', {
									variant: 'error',
									anchorOrigin: {
										vertical: 'top',
										horizontal: 'center',
									}
								});
							}
						}}
						fullWidth
					>
						Run Diagnostics
					</Button>
				</Box>

				<Typography variant="caption" style={{ marginTop: 16 }}>
					If problems persist, check your network connection or contact support.
				</Typography>
			</SpinnerContainer>
		);
	}

	// If call is established or there's no call in progress, show the dialer
	if (!connecting || callEstablished) {
		return (
			<DialerWrapper elevation={3}>
				<GridStyled container spacing={2}>
						<InputContainer>
							<Call>
								<CallIconWrapper>
									<CallIcon color="primary" />
								</CallIconWrapper>
								<InputBaseStyled
									placeholder='Enter SIP URI to call'
									value={requestUri || ''}
									onChange={(event) => setRequestUri(event.target.value)}
									onKeyPress={(ev) => {
										if (ev.key === 'Enter') {
											ev.preventDefault();
											if (registered && requestUri) {
												handleCall();
											}
										}
									}}
									autoFocus
									sx={{
										border: '1px solid',
										borderColor: 'divider',
										borderRadius: 1,
										pr: 1,
										height: '40px',
										width: '100%',
										transition: 'all 0.2s ease-in-out',
										'&:hover': {
											borderColor: 'primary.main',
										},
										'&:focus-within': {
											borderColor: 'primary.main',
											borderWidth: '2px',
											boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.1)',
										}
									}}
								/>
							</Call>
							<FormControl 
								size="small" 
								sx={{ 
									minWidth: { xs: '100%', sm: 120 },
									height: '40px'
								}}
							>
								<Select
									value={networkType}
									onChange={(e) => setNetworkType(e.target.value)}
									displayEmpty
									sx={{
										height: '40px',
										borderRadius: 1,
									}}
								>
									<MenuItem value="urgentnet">Urgentnet Conference</MenuItem>
									<MenuItem value="healthnet">Healthnet Conference</MenuItem>
									<MenuItem value="direct">Direct Call</MenuItem>
								</Select>
							</FormControl>
						</InputContainer>
						<Box sx={{ 
							mt: 2, 
							display: 'flex', 
							justifyContent: 'center',
							width: '100%'
						}}>
							<ActionButton
								variant='contained'
								color='primary'
								disabled={!registered || !requestUri}
								onClick={handleCall}
								size="large"
								startIcon={<CallIcon />}
							>
								Call
							</ActionButton>
						</Box>
						<LocalVideoPreviewer />
				</GridStyled>
			</DialerWrapper>
		);
	}

	return null;
};

Dialer.propTypes =
{
	sipCaller: PropTypes.any.isRequired
};

export default withMediaSipContext(Dialer);
